// =========================================================================
// HelpyDoc · Rol de administración del usuario que ha iniciado sesión
// =========================================================================
// El rol NO se guarda en la app ni en el perfil: viaja como custom claim
// `role` dentro del ID token que firma Firebase (lo pone
// `services/helpydoc-backend/scripts/admin-claim.mjs`). Eso importa por dos motivos:
//
//  1. No es falsificable desde el navegador. Alguien puede forzar que se pinte
//     el panel tocando el estado de React, pero no puede escribir nada: las
//     reglas de Storage comprueban el MISMO claim del lado del servidor. Esto
//     de aquí decide qué se ENSEÑA; la seguridad real está en `storage.rules`.
//  2. El claim entra en el token al emitirlo. Tras cambiar un rol hay que
//     volver a iniciar sesión (el script revoca los tokens para forzarlo).
//
// Jerarquía: `superadmin` (dirección general) cubre todo lo de `admin`.
//
// EN LOCAL no hay Firebase, así que no hay token ni claim y esto devolvía
// siempre `null`: el panel de administración era inalcanzable en desarrollo.
// La cuenta principal de la empresa lo recibe ahora en local — ver
// `rolLocalDeLaCuentaPrincipal()` más abajo.
// =========================================================================
import { AuthService, esCuentaPrincipalEnLocal } from '@/services/AuthService';

/** Roles con acceso a administración, de menor a mayor. */
export type RolAdmin = 'admin' | 'superadmin';

/** Rol del usuario; `null` = un paciente normal, sin acceso a nada de esto. */
export type RolUsuario = RolAdmin | null;

function esRolAdmin(valor: unknown): valor is RolAdmin {
  return valor === 'admin' || valor === 'superadmin';
}

/**
 * Rol de la cuenta principal de la empresa cuando se trabaja en local.
 *
 * `info@helpydoc.com` es el administrador principal y en PRODUCCIÓN lo es por
 * su custom claim `superadmin`. En local no hay Firebase que emita ese claim,
 * así que se refleja aquí para que el entorno de desarrollo se comporte como
 * el real. Es una decisión de Carlos (17-ago-2026): el panel tiene que poder
 * verse en local.
 *
 * Lo que NO cambia: la seguridad. Esto solo decide qué se ENSEÑA en un
 * navegador de desarrollo. Toda escritura sigue pasando por `storage.rules` y
 * `firestore.rules`, que comprueban el claim REAL del lado del servidor y
 * rechazan una sesión simulada. Un rol de mentira en local no escribe nada en
 * producción.
 *
 * Las tres guardas son las de `esCuentaPrincipalEnLocal()` y no se repiten
 * aquí a propósito: solo en `import.meta.env.DEV`, solo sin
 * `VITE_FIREBASE_API_KEY`, y solo para una lista explícita de un elemento. En
 * un build de producción esta rama no existe.
 */
function rolLocalDeLaCuentaPrincipal(): RolUsuario {
  try {
    const email = AuthService.getSession()?.email;
    if (!email) return null;
    return esCuentaPrincipalEnLocal(email) ? 'superadmin' : null;
  } catch {
    return null;
  }
}

/**
 * Lee el rol del ID token en curso. Nunca lanza: ante cualquier problema
 * (sin sesión, sin red, Firebase apagado en desarrollo) devuelve `null`, que
 * es el caso seguro — sin panel.
 */
export async function rolDelUsuario(): Promise<RolUsuario> {
  // Primero lo local, y con retorno inmediato: si estamos en desarrollo sin
  // Firebase, cargar el módulo de Firebase para que diga que está apagado es
  // trabajo tirado. Las guardas garantizan que en producción esto es `null`.
  const local = rolLocalDeLaCuentaPrincipal();
  if (local) return local;

  try {
    const { isFirebaseEnabled, waitForFirebaseReady } = await import('@/services/firebase');
    if (!isFirebaseEnabled) return null;
    await waitForFirebaseReady();
    const { getAuth } = await import('firebase/auth');
    const user = getAuth().currentUser;
    if (!user) return null;
    // Sin forzar refresco: el token en memoria ya trae el claim, y forzarlo en
    // cada arranque es una llamada de red por sesión a cambio de nada. Cuando
    // el rol cambia, el script revoca los tokens y el siguiente login lo trae.
    const { claims } = await user.getIdTokenResult();
    return esRolAdmin(claims.role) ? claims.role : null;
  } catch {
    return null;
  }
}

/** ¿Puede administrar contenido (platos, fotos, consejos)? */
export function puedeAdministrarContenido(rol: RolUsuario): boolean {
  return rol === 'admin' || rol === 'superadmin';
}

/** ¿Es la dirección general? Reservado a lo que solo ese nivel puede tocar. */
export function esSuperadmin(rol: RolUsuario): boolean {
  return rol === 'superadmin';
}
